import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { VolumeUp } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";
import WizardPageShell from "./WizardPageShell";

export default function WizardDailyWordChallenge() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [sentence, setSentence] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("student/wizard/daily-word/today");
      const data = res.data?.data || null;
      setPayload(data);
      if (data?.submission?.sentence) {
        setSentence(String(data.submission.sentence));
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "Could not load today's word.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const speakWord = () => {
    const word = payload?.word?.word;
    if (!word || typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(String(word));
    utter.lang = "en-GB";
    window.speechSynthesis.speak(utter);
  };

  const submit = async () => {
    const trimmed = sentence.trim();
    if (trimmed.length < 8) {
      toast.error("Write a sentence of at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("student/wizard/daily-word/submit", { sentence: trimmed });
      setPayload(res.data?.data || null);
      toast.success("Sentence reviewed.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not submit your sentence.");
    } finally {
      setSubmitting(false);
    }
  };

  const word = payload?.word;
  const submission = payload?.submission;

  return (
    <WizardPageShell
      title="Daily Word Challenge"
      subtitle="Learn a new word every day and test yourself"
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error && !word ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Stack spacing={2.5}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 3,
              background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
              border: "1px solid #DDD6FE",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h4" fontWeight={800} color="#5B21B6">
                {word?.word || "—"}
              </Typography>
              <Button
                size="small"
                startIcon={<VolumeUp />}
                onClick={speakWord}
                sx={{ textTransform: "none" }}
              >
                Listen
              </Button>
            </Stack>
            <Typography sx={{ mb: 1.5 }}>{word?.definition}</Typography>
            {word?.example ? (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Example: {word.example}
              </Typography>
            ) : null}
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
              {(word?.synonyms || []).map((s) => (
                <Chip key={`syn-${s}`} size="small" label={`≈ ${s}`} />
              ))}
              {(word?.antonyms || []).map((s) => (
                <Chip key={`ant-${s}`} size="small" variant="outlined" label={`≠ ${s}`} />
              ))}
            </Stack>
          </Box>

          <TextField
            multiline
            minRows={3}
            fullWidth
            label="Use today's word in a sentence"
            helperText={`${sentence.trim().length}/600 (min 8)`}
            value={sentence}
            onChange={(e) => setSentence(e.target.value.slice(0, 600))}
          />
          <Button
            variant="contained"
            disabled={submitting}
            onClick={submit}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              alignSelf: "flex-start",
              bgcolor: "#D97706",
              "&:hover": { bgcolor: "#B45309" },
            }}
          >
            {submitting ? "Checking…" : submission ? "Submit again" : "Submit sentence"}
          </Button>

          {submission ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#FFFBEB",
                border: "1px solid #FDE68A",
              }}
            >
              <Typography fontWeight={800} sx={{ mb: 1 }}>
                AI Coach feedback · {Math.round(Number(submission.score) || 0)}%
              </Typography>
              <Chip
                size="small"
                color={submission.used_correctly ? "success" : "warning"}
                label={submission.used_correctly ? "Used correctly" : "Needs work"}
                sx={{ mb: 1 }}
              />
              <Typography sx={{ mb: 1 }}>{submission.feedback}</Typography>
              {(submission.strengths || []).map((s, i) => (
                <Typography key={i} variant="body2">
                  ✓ {s}
                </Typography>
              ))}
              {(submission.improvements || []).map((s, i) => (
                <Typography key={`i-${i}`} variant="body2" color="text.secondary">
                  → {s}
                </Typography>
              ))}
              {submission.improved_sentence ? (
                <Typography variant="body2" sx={{ mt: 1 }} fontStyle="italic">
                  Suggested: {submission.improved_sentence}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Stack>
      )}
    </WizardPageShell>
  );
}
